/*!
 * Copyright © 2019-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { EDriverWorkingState } from 'tacho';
import { isElementNull } from '../../../../utils';

import { IDriverStatus, IMapContextMenuData, IPoint, IZoomToMarkerParams, MarkerInfo } from '../../../core/models';

@Component({
  selector: 'fm-driver-status-card',
  templateUrl: './driver-status-card.component.html',
  styleUrls: ['./driver-status-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DriverStatusCardComponent {
  @Input()
  public set driverStatus(value: IDriverStatus) {
    this._driverStatus = value;
    this._highlightDriver(this._driverStatus.vehicleTachoInfo.isUpdating);
  }
  public get driverStatus(): IDriverStatus {
    return this._driverStatus;
  }

  @Output()
  public highlightMarker = new EventEmitter<number>();
  @Output()
  public removeMarkerHighlight = new EventEmitter<void>();
  @Output()
  public openDriverStatusContextMenu = new EventEmitter<IMapContextMenuData<MarkerInfo>>();
  @Output()
  public triggerZoomTo = new EventEmitter<IZoomToMarkerParams>();

  public EDriverWorkingState = EDriverWorkingState;

  private _driverStatus: IDriverStatus;

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {}

  public onDriverMouseEnter(): void {
    if (
      !isElementNull(this._driverStatus.vehicleTachoInfo.status) &&
      !isElementNull(this._driverStatus.vehicleTachoInfo.status.VehicleId)
    ) {
      this.highlightMarker.emit(this._driverStatus.vehicleTachoInfo.status.VehicleId);
    }
  }

  public onStatusCardRightClick($event: MouseEvent): void {
    const mapElement = <HTMLElement>this._document.querySelector('div.fm-map-container');
    const clickPosition: IPoint = {
      x: $event.pageX,
      y: $event.pageY - mapElement.getBoundingClientRect().top
    };

    const driverStatusContextMenuData: IMapContextMenuData<MarkerInfo> = this._prepareContextMenuData(clickPosition);
    this.openDriverStatusContextMenu.emit(driverStatusContextMenuData);
  }

  public onStatusCardMenuIconClick($event: MouseEvent): void {
    $event.stopPropagation();
    const mapElement = <HTMLElement>this._document.querySelector('div.fm-map-container');
    const targetElement = (($event.target as Element).parentNode as HTMLElement).getBoundingClientRect() as DOMRect;
    const margin = targetElement.width + 8;
    const clickPosition: IPoint = {
      x: targetElement.left + margin,
      y: targetElement.top - mapElement.getBoundingClientRect().top
    };

    const driverStatusContextMenuData: IMapContextMenuData<MarkerInfo> = this._prepareContextMenuData(clickPosition);
    this.openDriverStatusContextMenu.emit(driverStatusContextMenuData);
  }

  public onDriverMouseLeave(): void {
    this.removeMarkerHighlight.emit();
  }

  public onDriverCardClick(): void {
    this.triggerZoomTo.emit({
      latLng: this._driverStatus.markerInfo.latLng,
      id: this._driverStatus.vehicleTachoInfo.status.VehicleId,
      type: this._driverStatus.markerInfo.type,
      title: this._driverStatus.vehicleName
    });
  }

  public getVehicleIdSelector(id: number): string {
    return `li-driver-status-${id}`;
  }

  private _prepareContextMenuData(menuLocation: IPoint): IMapContextMenuData<MarkerInfo> {
    if (this._driverStatus.markerInfo) {
      const vehicleLatLng = this._driverStatus.markerInfo.latLng;
      const vehicleContextMenuData: IMapContextMenuData<MarkerInfo> = {
        clickPosition: menuLocation,
        clickLatLng: vehicleLatLng,
        streetViewAvailable: false,
        markerInfo: this._driverStatus.markerInfo
      };

      return vehicleContextMenuData;
    }
    return null;
  }

  private _highlightDriver(isUpdating: boolean): void {
    const defaultColor = '#ffffff';
    const highlightColor = 'rgba(63, 156, 244, .2)';
    setTimeout(() => {
      const driverElement = this._document.getElementById(this.getVehicleIdSelector(this._driverStatus.vehicleTachoInfo.status.VehicleId));
      if (driverElement) {
        if (isUpdating) {
          driverElement.style.backgroundColor = highlightColor;
        } else {
          driverElement.style.backgroundColor = defaultColor;
        }
      }
    });
  }
}
